import { RoomSchemeSize } from "@/dom/components/RoomSchemeSize/RoomSchemeSize";
import { useDemoState } from "@/state/demoState";
import {
    getWallPosition,
    getWallRotation,
    isWallKey,
    RoomConfig,
} from "@/state/RoomConfig/RoomConfig";
import { THEME_COLORS } from "@/utils/colors";
import { Html, Line } from "@react-three/drei";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Color, Euler, Quaternion, Vector2Like } from "three";
import { LineSegments2 } from "three-stdlib";
import {
    createSelectedWall,
    SELECTED_ITEM_TYPE,
    checkIsSelectedWall,
    checkIsSelectedWallOrWallProp,
    checkIsSelectedWallProp,
} from "../SelectedItem";
import { SelectedItem } from "../SelectedItem";
import { WallConfig, WallPropConfig } from "@/state/RoomConfig/WallConfig";
import { WallArrow } from "@/dom/Demo/RoomSetup/WallArrow";
import { useMouseInteractions } from "@/utils/useMouseInteractions";
import { updateRoomSizeByCursorPoint } from "./updateRoomSizeByCursorPoint";
import { useWallPropDragging } from "./useWallPropDragging";
import { useRoomSizeRestrictions } from "@/utils/useRoomSizeRestrictions";

const WALL_COLOR_SELECTED = THEME_COLORS.generalBlack;
const WALL_PROP_COLOR = new Color(1, 1, 1);
const WALL_PROP_OUTLINE_COLOR_HIGHLIGHTED: Color = new Color(17 / 255, 100 / 255, 1);
const LINE_WIDTH_DEFAULT = 1;
const LINE_WIDTH_HIGHLIGHTED = 2;

type Props = {
    cameraRef: React.RefObject<Camera>;
};

export const RoomScheme: React.FC<Props> = ({ cameraRef }) => {
    const storeRoomConfig = useDemoState((state) => state.roomConfig);
    const setStoreRoomConfig = useDemoState((state) => state.setRoomConfig);

    const [roomConfig, setRoomConfig] = useState(storeRoomConfig);

    useEffect(() => {
        setRoomConfig(storeRoomConfig);
    }, [storeRoomConfig]);

    const view = useDemoState((state) => state.view);
    const step = useDemoState((state) => state.step);
    const layerHeight = roomConfig.height;

    const selectedItem = useDemoState((state) => state.selectedItem);
    const setSelectedItem = useDemoState((state) => state.setSelectedItem);

    const {
        handleDragStart: handleWallPropDragStart,
        handleDrag: handleWallPropDrag,
        handleDragEnd: handleWallPropDragEnd,
    } = useWallPropDragging(storeRoomConfig);

    const roomSizeRestrictions = useRoomSizeRestrictions();

    const isWallSelected = (key: string) =>
        selectedItem !== undefined &&
        ((checkIsSelectedWall(selectedItem) && selectedItem.key === key) ||
            (checkIsSelectedWallProp(selectedItem) && selectedItem.parent === key));

    const handleDragStart = useCallback(
        (item: SelectedItem): boolean => {
            if (selectedItem === undefined) {
                return false;
            }

            const isSelectedItemDragging =
                "key" in item && "key" in selectedItem && item.key === selectedItem.key;

            if (!isSelectedItemDragging) {
                return false;
            }

            if (checkIsSelectedWallProp(item)) {
                handleWallPropDragStart(item);
            }

            return true;
        },
        [selectedItem, handleWallPropDragStart]
    );

    const handleDrag = useCallback(
        (item: SelectedItem, target: Vector2Like) => {
            if (checkIsSelectedWall(item)) {
                const updatedRoomConfig = updateRoomSizeByCursorPoint(
                    item.key,
                    roomConfig,
                    target,
                    roomSizeRestrictions
                );
                setRoomConfig(updatedRoomConfig);
            }

            if (checkIsSelectedWallProp(item) && item.parent && isWallKey(item.parent)) {
                const updatedProp = handleWallPropDrag(target);
                if (!updatedProp) return;

                setRoomConfig({
                    ...roomConfig,
                    [item.parent]: {
                        ...roomConfig[item.parent],
                        props: roomConfig[item.parent].props.map((prop) =>
                            prop.key === item.key ? updatedProp : prop
                        ),
                    },
                });
            }
        },
        [roomConfig, setRoomConfig, handleWallPropDrag, roomSizeRestrictions]
    );

    const handleDragEnd = useCallback(() => {
        handleWallPropDragEnd();
        setStoreRoomConfig(roomConfig);
    }, [roomConfig, setStoreRoomConfig, handleWallPropDragEnd]);

    const handleClick = useCallback((item: SelectedItem | undefined) => {
        if (!item) {
            setSelectedItem(undefined);
            return;
        }

        if (!checkIsSelectedWallOrWallProp(item)) return;

        setSelectedItem(item);
    }, []);

    useMouseInteractions({
        cameraRef,
        onDrag: handleDrag,
        onDragStart: handleDragStart,
        onClick: handleClick,
        filter: (item) =>
            item.selectedItemType === SELECTED_ITEM_TYPE.WALL_PROP ||
            item.selectedItemType === SELECTED_ITEM_TYPE.WALL,
        onDragEnd: handleDragEnd,
        isEnabled: view === "2d" && step === "room setup",
    });

    return (
        <group visible={view === "2d"} position={[0, layerHeight, 0]}>
            <RoomUnderlay config={roomConfig} layerHeight={layerHeight} />
            <Floor config={roomConfig} layerHeight={layerHeight} />
            <Wall
                isSelected={isWallSelected(roomConfig.leftWall.key)}
                size={roomConfig.depth}
                position={getWallPosition(roomConfig.leftWall, roomConfig, {
                    includeThickness: true,
                })}
                rotation={getWallRotation(roomConfig.leftWall, roomConfig)}
                config={roomConfig.leftWall}
                selectedItem={selectedItem}
            />
            <Wall
                isSelected={isWallSelected(roomConfig.rightWall.key)}
                size={roomConfig.depth}
                position={getWallPosition(roomConfig.rightWall, roomConfig, {
                    includeThickness: true,
                })}
                rotation={getWallRotation(roomConfig.rightWall, roomConfig)}
                config={roomConfig.rightWall}
                selectedItem={selectedItem}
            />
            <Wall
                isSelected={isWallSelected(roomConfig.frontWall.key)}
                size={roomConfig.width}
                position={getWallPosition(roomConfig.frontWall, roomConfig, {
                    includeThickness: true,
                })}
                rotation={getWallRotation(roomConfig.frontWall, roomConfig)}
                config={roomConfig.frontWall}
                selectedItem={selectedItem}
            />
            <Wall
                isSelected={isWallSelected(roomConfig.backWall.key)}
                size={roomConfig.width}
                position={getWallPosition(roomConfig.backWall, roomConfig, {
                    includeThickness: true,
                })}
                rotation={getWallRotation(roomConfig.backWall, roomConfig)}
                config={roomConfig.backWall}
                selectedItem={selectedItem}
            />
        </group>
    );
};

type RoomUnderlayProps = {
    config: RoomConfig;
    layerHeight: number;
};

export const RoomUnderlay: React.FC<RoomUnderlayProps> = ({ config, layerHeight }) => {
    const width = config.width + config.leftWall.thickness + config.rightWall.thickness;
    const depth = config.depth + config.frontWall.thickness + config.backWall.thickness;
    const left = (config.rightWall.thickness - config.leftWall.thickness) / 2;
    const top = (config.frontWall.thickness - config.backWall.thickness) / 2;

    return (
        <mesh position={[left, -layerHeight - 1.1, top]} scale={[width, 0.1, depth]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial toneMapped={false} color={config.leftWall.color} />
        </mesh>
    );
};

export const Floor: React.FC<RoomUnderlayProps> = ({ config, layerHeight }) => {
    const width = config.width;
    const depth = config.depth;

    return (
        <mesh position={[0, -layerHeight - 1, 0]} scale={[width, 0.1, depth]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial toneMapped={false} color={config.floorColor} />
        </mesh>
    );
};

type WallProps = {
    size: number;
    isSelected: boolean;
    position: [x: number, y: number, z: number];
    rotation: number;
    config: WallConfig;
    selectedItem?: SelectedItem;
};

export const Wall: React.FC<WallProps> = ({
    size,
    isSelected,
    position,
    rotation,
    config,
    selectedItem,
}) => {
    const selectedPropIndex: number | undefined = useMemo(() => {
        if (selectedItem?.selectedItemType !== SELECTED_ITEM_TYPE.WALL_PROP) {
            return undefined;
        }

        const index = config.props.findIndex((prop) => prop.key === selectedItem.key);

        return index > -1 ? index : undefined;
    }, [selectedItem]);

    const sizes = useMemo(() => {
        if (selectedPropIndex === undefined) {
            return [{ min: 0, max: size, value: size * 1000 }];
        }

        const prop = config.props[selectedPropIndex];

        return [
            { min: 0, max: prop.offset, value: prop.offset * 1000 },
            {
                min: prop.offset,
                max: prop.offset + prop.width,
                value: prop.width * 1000,
            },
            {
                min: prop.offset + prop.width,
                max: size,
                value: (size - prop.offset - prop.width) * 1000,
            },
        ];
    }, [selectedPropIndex, size, config]);

    const getPropState = (prop: WallPropConfig) => {
        if (isSelected) {
            return selectedItem?.selectedItemType === SELECTED_ITEM_TYPE.WALL_PROP &&
                selectedItem.key === prop.key
                ? "element-highlighted"
                : "wall-highlighted";
        }

        return "default";
    };

    return (
        <group position={position} rotation={[0, rotation, 0]}>
            <mesh scale={[size, 0.1, config.thickness]} userData={createSelectedWall(config)}>
                <boxGeometry args={[1, 1, 1]} />
                <meshBasicMaterial
                    toneMapped={false}
                    color={isSelected ? WALL_COLOR_SELECTED : config.color}
                />
            </mesh>
            <group position={[0, 0, config.thickness / 2]}>
                <Html center>
                    <WallArrow hidden={!isSelected} wallKey={config.key} />
                </Html>
            </group>
            <group position={[0, 0, -config.thickness / 2]}>
                <Html center>
                    <WallArrow mirrored hidden={!isSelected} wallKey={config.key} />
                </Html>
            </group>
            <group position={[-size / 2, 0, 0]}>
                {config.props.map((prop, index) => (
                    <WallProp
                        key={index}
                        config={prop}
                        state={getPropState(prop)}
                        width={config.thickness}
                        wall={config}
                    />
                ))}

                <group position={[0, 0, -config.thickness / 2]}>
                    {sizes.map((size, index) => (
                        <SizeView key={index} min={size.min} max={size.max} value={size.value} />
                    ))}
                </group>
            </group>
        </group>
    );
};

type WallPropProps = {
    state: "default" | "wall-highlighted" | "element-highlighted";
    config: WallPropConfig;
    wall: WallConfig;
    width: number;
};

export const WallProp: React.FC<WallPropProps> = ({ width, state, config, wall }) => {
    const size = config.width;
    const xMin = 0;
    const xMax = size;
    const zMin = width / 2;
    const zMax = -width / 2;

    return (
        <group position={[config.offset, 0, 0]}>
            <mesh
                scale={[size, 0.1, width]}
                position={[size / 2, 0.02, 0]}
                userData={{
                    selectedItemType: SELECTED_ITEM_TYPE.WALL_PROP,
                    parent: wall.key,
                    key: config.key,
                }}
            >
                <boxGeometry args={[1, 1, 1]} />
                <meshBasicMaterial toneMapped={false} color={WALL_PROP_COLOR} />
            </mesh>
            <Line
                points={[
                    [xMin, 0.1, zMax],
                    [xMax, 0.1, zMax],
                    [xMax, 0.1, zMax],
                    [xMax, 0.1, zMin],
                    [xMin, 0.1, zMin],
                    [xMin, 0.1, zMax],
                ]}
                color={
                    state === "element-highlighted"
                        ? WALL_PROP_OUTLINE_COLOR_HIGHLIGHTED
                        : config.highlightColor
                }
                lineWidth={state !== "default" ? LINE_WIDTH_HIGHLIGHTED : LINE_WIDTH_DEFAULT}
            />
            <Line
                visible={config.type === "window"}
                points={[
                    [xMin, 0.1, 0],
                    [xMax, 0.1, 0],
                ]}
                color={
                    state === "element-highlighted"
                        ? WALL_PROP_OUTLINE_COLOR_HIGHLIGHTED
                        : config.highlightColor
                }
                lineWidth={LINE_WIDTH_HIGHLIGHTED}
            />
        </group>
    );
};

type SizeViewProps = {
    value: number;
    min: number;
    max: number;
};

const SIZE_VIEW_THICKNESS = 0.4;
const SIZE_VIEW_OFFSET = 0.2;

export const SizeView: React.FC<SizeViewProps> = ({ value, min, max }) => {
    const view = useDemoState((state) => state.view);
    const lineRef = useRef<LineSegments2>(null);

    const [isTextRotated, setIsTextRotated] = useState(false);

    const xMin = 0;
    const xMax = 1;
    const zMin = -0.5;
    const zMax = 0.5;
    const zCenter = -SIZE_VIEW_OFFSET - SIZE_VIEW_THICKNESS / 2;

    useEffect(() => {
        if (lineRef.current) {
            const quaternion = new Quaternion();
            lineRef.current.getWorldQuaternion(quaternion);
            const euler = new Euler();
            euler.setFromQuaternion(quaternion);

            const yRotation = euler.y;
            const deg90 = Math.PI / 2;
            const isRotated = Math.abs(yRotation * yRotation - deg90 * deg90) < 0.001;

            setIsTextRotated(isRotated);
        }
    }, [lineRef]);

    return (
        <group position={[min, 0, zCenter]} scale={[max - min, 1, SIZE_VIEW_THICKNESS]}>
            <Line
                points={[
                    [xMin, 0, 0],
                    [xMax, 0, 0],
                    [xMin, 0, zMin],
                    [xMin, 0, zMax],
                    [xMax, 0, zMin],
                    [xMax, 0, zMax],
                ]}
                segments
                color={THEME_COLORS.generalBlack}
                ref={lineRef}
            />
            <group position={[(xMax + xMin) / 2, 0, 0]}>
                <Html center zIndexRange={[0, 0]}>
                    <RoomSchemeSize
                        value={value}
                        isRotated={isTextRotated}
                        isVisible={view === "2d"}
                    />
                </Html>
            </group>
        </group>
    );
};
